/**
 * @file hints.ts
 * @description Реестр текстов-подсказок стартового шаблона и поиск их в
 * payload секции (phase19.28 этап А; решение пользователя 2026-10-24 по
 * uiux-starter-site, вопрос 2): подсказки [З] остаются текстом, а
 * конструктор предупреждает «На странице остался текст шаблона: …» в окне
 * публикации (как SB127) и в условиях первого открытия (PL9). Это
 * предупреждение, не запрет (SB120); показ — дело кабинета и сервера.
 *
 * Сравнение по нормализованной строке: без краевых пробелов и регистра,
 * многоточия и точка в конце не важны, «ё» = «е», пробелы схлопнуты.
 * Текст считается оставленным, если подсказка целиком входит в него (владелец
 * дописал к подсказке своё — подсказка всё равно видна покупателю).
 */

import type { SectionPayload } from "../site-section/schema.js";
import { STARTER_HINT } from "./standard-texts.js";

/** Все подсказки [З] шаблонов платформы, без повторов. */
export const STARTER_HINT_TEXTS: readonly string[] = Object.freeze([...new Set<string>(Object.values(STARTER_HINT))]);

/** Нормализация для сравнения с реестром. */
export function normalizeStarterText(value: string): string {
  return value
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/…|\.{3,}/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[.!?]+$/, "")
    .trim();
}

/** Видимый текст секции: путь в payload и строка. */
export interface SectionText {
  path: string;
  text: string;
}

/**
 * Ключи payload, под которыми лежит текст, видимый покупателю: заголовки,
 * абзацы, надписи, подписи кнопок и ссылок, вопросы и ответы, плитки,
 * подписи фото, ячейки таблиц. Обход общий (а не по видам элементов), чтобы
 * новый вид с теми же ключами попадал в проверку без правок здесь.
 */
const TEXT_KEYS: ReadonlySet<string> = new Set(["text", "title", "label", "eyebrow", "q", "a", "buttonLabel", "alt", "cells"]);

function walk(value: unknown, path: string, textual: boolean, out: SectionText[]): void {
  if (typeof value === "string") {
    if (textual && value.trim() !== "") out.push({ path, text: value });
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => walk(item, `${path}.${index}`, textual, out));
    return;
  }
  if (typeof value === "object" && value !== null) {
    for (const [key, nested] of Object.entries(value)) {
      walk(nested, path === "" ? key : `${path}.${key}`, TEXT_KEYS.has(key), out);
    }
  }
}

/** Все видимые тексты секции по порядку payload. */
export function collectSectionTexts(payload: SectionPayload): SectionText[] {
  const out: SectionText[] = [];
  walk(payload, "", false, out);
  return out;
}

/** Подсказки реестра, которые остались в секции (по порядку появления, без повторов). */
export function findStarterHintsInPayload(
  payload: SectionPayload,
  hints: readonly string[] = STARTER_HINT_TEXTS,
): string[] {
  const normalized = hints.map((hint) => ({ hint, key: normalizeStarterText(hint) })).filter((h) => h.key !== "");
  const found: string[] = [];
  for (const { text } of collectSectionTexts(payload)) {
    const value = normalizeStarterText(text);
    for (const { hint, key } of normalized) {
      if (value.includes(key) && !found.includes(hint)) found.push(hint);
    }
  }
  return found;
}
